import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatApiError, money, fmtDate } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { PriceDialog, FilingDialog } from "@/components/AdminDialogs";
import { BillingDialog, DocsDialog } from "@/components/AdminBilling";
import { AdminAccounting } from "@/components/AdminAccounting";
import { AdminClaims } from "@/components/AdminClaims";
import { AdminSupport, IdentityDialog } from "@/components/AdminSupport";
import { AutomationBar } from "@/components/AutomationBar";
import { StripeSetup } from "@/components/StripeSetup";
import { PolicyDocButtons } from "@/components/PolicyDocButtons";
import { MarketingPanel } from "@/components/MarketingPanel";
import { IssueFreeDialog } from "@/components/AdminComp";
import { Link } from "react-router-dom";
import { coverageLabel, reasonLabel, levelLabel, limitLabel } from "@/lib/constants";

const Th = ({ children }) => <th className="whitespace-nowrap p-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{children}</th>;
const Td = ({ children, className = "", ...rest }) => <td className={`whitespace-nowrap p-3 ${className}`} {...rest}>{children}</td>;
const Table = ({ head, children, testId }) => (
  <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
    <table className="w-full text-sm" data-testid={testId}>
      <thead className="bg-slate-50"><tr>{head.map((h) => <Th key={h}>{h}</Th>)}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const match = (q, ...fields) => !q || fields.some((f) => String(f || "").toLowerCase().includes(q.toLowerCase()));

const Stats = ({ s }) => (
  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
    {[["Customers", s.customers], ["Quotes to price", s.quotes_pending], ["Active policies", s.active_policies], ["Filings in progress", s.filings_in_progress], ["Revenue (paid)", money(s.revenue)]].map(([k, v]) => (
      <div key={k} className="rounded-lg border border-slate-200 bg-white p-4">
        <p className="text-xs uppercase tracking-wider text-slate-500">{k}</p>
        <p className="mt-1 font-mono text-2xl font-semibold" data-testid={`admin-stat-${k.split(" ")[0].toLowerCase()}`}>{v}</p>
      </div>
    ))}
  </div>
);

export default function Admin() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const load = useCallback(() => {
    Promise.all([api.get("/admin/stats"), api.get("/admin/quotes"), api.get("/admin/policies"), api.get("/admin/customers")])
      .then(([s, qs, ps, cs]) => setD({ stats: s.data, quotes: qs.data, policies: ps.data, customers: cs.data }))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  if (!d) return null;
  return (
    <div className="mx-auto max-w-[1800px] px-4 py-10 sm:px-6" data-testid="admin-dashboard">
      <p className="eyebrow">Admin console</p>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight">Operations</h1>
      <Link to="/quote" className="mt-3 inline-block text-sm font-semibold text-blue-700 underline" data-testid="admin-new-quote-link">+ Create a quote for yourself or someone else, then issue it free from the Quotes tab</Link>
      <AutomationBar />
      <StripeSetup />
      <div className="mt-6"><Stats s={d.stats} /></div>
      <Tabs defaultValue="quotes" className="mt-8">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <TabsList className="h-auto flex-wrap justify-start" data-testid="admin-tabs-list">
            <TabsTrigger value="quotes" data-testid="admin-tab-quotes">Quotes</TabsTrigger>
            <TabsTrigger value="policies" data-testid="admin-tab-policies">Policies & filings</TabsTrigger>
            <TabsTrigger value="claims" data-testid="admin-tab-claims">Claims desk</TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-tab-messages">Messages</TabsTrigger>
            <TabsTrigger value="customers" data-testid="admin-tab-customers">Customers</TabsTrigger>
            <TabsTrigger value="accounting" data-testid="admin-tab-accounting">Accounting</TabsTrigger>
            <TabsTrigger value="marketing" data-testid="admin-tab-marketing">Marketing</TabsTrigger>
          </TabsList>
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, state…" className="pl-9" data-testid="admin-search-input" />
          </div>
        </div>
        <TabsContent value="quotes" className="mt-4">
          <Table testId="admin-quotes-table" head={["Submitted", "Driver", "State", "Type", "Reason", "Coverage", "Estimate", "Final", "Status", ""]}>
            {d.quotes.filter((x) => match(q, x.full_name, x.user_email, x.state)).map((x) => (
              <tr key={x.id} className="border-t border-slate-100" data-testid="admin-quote-row">
                <Td>{fmtDate(x.created_at)}</Td>
                <Td><p className="font-semibold">{x.full_name}</p><p className="text-xs text-slate-500">{x.user_email} · {x.phone}</p></Td>
                <Td className="font-mono">{x.state}</Td>
                <Td>{coverageLabel(x.coverage_type)}</Td>
                <Td>{x.coverage_type === "general_liability" ? "—" : reasonLabel(x.reason)}</Td>
                <Td>{x.coverage_type === "general_liability" ? "CGL" : levelLabel(x.coverage_level)}<p className="text-xs text-slate-500">{limitLabel(x.liability_limit)}{x.business_name ? ` · ${x.business_name} (${x.coverage_type === "general_liability" ? `${x.employee_count} emp.` : `${x.vehicle_count} veh.`})` : ""}</p></Td>
                <Td className="font-mono">${x.estimate_low}–${x.estimate_high}{x.bundle_discount && <p className="text-xs text-emerald-700">Bundle −10%</p>}</Td>
                <Td className="font-mono">{x.monthly_premium ? money(x.monthly_premium) : "—"}</Td>
                <Td><StatusPill status={x.status} /></Td>
                <Td><div className="flex gap-1"><DocsDialog quote={x} />{x.status !== "purchased" && <PriceDialog quote={x} onDone={load} />}{!["purchased", "declined"].includes(x.status) && <IssueFreeDialog quote={x} onDone={load} />}</div></Td>
              </tr>
            ))}
          </Table>
        </TabsContent>
        <TabsContent value="policies" className="mt-4">
          <Table testId="admin-policies-table" head={["Policy #", "Insured", "State", "Type", "Premium", "Effective", "Filing status", ""]}>
            {d.policies.filter((x) => match(q, x.insured_name, x.user_email, x.state, x.policy_number)).map((x) => (
              <tr key={x.id} className="border-t border-slate-100" data-testid="admin-policy-row">
                <Td className="font-mono">{x.policy_number}</Td>
                <Td><p className="font-semibold">{x.insured_name}</p><p className="text-xs text-slate-500">{x.user_email}</p></Td>
                <Td className="font-mono">{x.state}</Td>
                <Td>{coverageLabel(x.coverage_type)}</Td>
                <Td className="font-mono">{money(x.monthly_premium)}</Td>
                <Td>{fmtDate(x.effective_date)}</Td>
                <Td><StatusPill status={x.filing_status} /></Td>
                <Td><div className="flex gap-1"><BillingDialog policy={x} onDone={load} /><FilingDialog policy={x} onDone={load} /><PolicyDocButtons policy={x} size="sm" idSuffix={x.id} compact /></div></Td>
              </tr>
            ))}
          </Table>
        </TabsContent>
        <TabsContent value="customers" className="mt-4">
          <Table testId="admin-customers-table" head={["Name", "Email", "Phone", "Joined", "Quotes", "Policies", "Identity", ""]}>
            {d.customers.filter((x) => match(q, x.name, x.email)).map((x) => (
              <tr key={x.id} className="border-t border-slate-100" data-testid="admin-customer-table-row">
                <Td className="font-semibold">{x.name}</Td>
                <Td>{x.email}</Td>
                <Td>{x.phone || "—"}</Td>
                <Td>{fmtDate(x.created_at)}</Td>
                <Td className="font-mono">{x.quotes}</Td>
                <Td className="font-mono">{x.policies}</Td>
                <Td data-testid={`admin-identity-status-${x.id}`}>{x.ssn_on_file && x.state_id_on_file ? <span className="font-semibold text-emerald-700">Verified</span> : <span className="text-amber-700">{[!x.ssn_on_file && "Missing SSN", !x.state_id_on_file && "Missing ID"].filter(Boolean).join(" · ")}</span>}</Td>
                <Td><IdentityDialog customer={x} /></Td>
              </tr>
            ))}
          </Table>
        </TabsContent>
        <TabsContent value="claims" className="mt-4">
          <AdminClaims search={q} />
        </TabsContent>
        <TabsContent value="messages" className="mt-4">
          <AdminSupport search={q} />
        </TabsContent>
        <TabsContent value="marketing" className="mt-4">
          <MarketingPanel />
        </TabsContent>
        <TabsContent value="accounting" className="mt-4">
          <AdminAccounting />
        </TabsContent>
      </Tabs>
    </div>
  );
}
